import {
  AppInsightsContext,
  AppInsightsErrorBoundary,
} from '@microsoft/applicationinsights-react-js'
import { AuditActionEnum } from '@rsmus/ecp-userservice'
import {
  Anonymous,
  Authenticated,
  getAccessToken,
  OidcIdentity,
} from '@rsmus/react-auth'
import { Eula } from '@rsmus/rsm-eula'
import React, { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Box } from '@mui/material'

import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { Styles } from './types'
import Unavailable from './pages/Unavailable'
import ProtectedRouteError from './components/ProtectedRouteError'
import SessionManager from './components/SessionManager'
import VersionService from './components/VersionService'
import {
  APP_REACT_AUTH_AUTHORITY,
  APP_REACT_AUTH_CLIENTID,
  APP_REACT_AUTH_SCOPE,
  IDM_EULA_SERVICE_URL,
} from './envVariables'
import { ScrollToTop } from './rsmCoreComponents'
import Refocus from './rsmCoreComponents/components/ReFocus'
import { rcPlugin } from './rsmCoreComponents/hooks/appInsights'
import {
  AuditLoginTopic,
  subscribeToAuditTopics,
} from './utils/helpers/Audit.service'
import { publish } from './utils/helpers/PubSub.service'
import './index.css'
import DeepLink from './components/DeepLink/DeepLink'
import { setRedirectUrl } from './store/deepLink/deepLinkSlice'
import TrackingManager from './components/TrackingManager/TrackingManager'
import { Interceptor } from './api/Interceptor/Interceptor'
import { AppRoutes } from './AppRoutes'
import Unsubscribe from './pages/Unsubscribe/Unsubscribe'
import ProtectedRoute from './components/Unsubscribe/UnsubscribeProtectedRoute'
import UnsubscribeSuccess from './components/Unsubscribe/UnsubscribeSuccess'

const styles: Styles = {
  // Style for Okta app authorization failure message container.
  unavailableContainer: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.common.white,
    height: '33.5rem',
  }),
}

const autoLogin = true
const autoRedirect = true
// Force the rsm logo to display in the Unavailable component
// when user authorization fails.
const showRsmLogoOnAuthFailure = true
const redirectURI = 'https://rsmus.com'

// Query string parameter and value constants.
// Okta produces these when the user is authenticated
// but not authorized for the application.
const errorDescriptionQueryStringParm = 'error_description'
const unauthorizedForAppErrorMessage =
  'User+is+not+assigned+to+the+client+application.'
const userCreationDisabledErrorMessage = 'User+creation+was+disabled.'

const App = () => {
  const dispatch = useDispatch()
  const getToken = async () => {
    const result = await getAccessToken()
    return result || ''
  }

  useEffect(() => {
    subscribeToAuditTopics()
  }, [])

  const handleRedirectCallback = (url: string) => {
    publish(AuditLoginTopic, AuditActionEnum.LoginSucceeded)

    const cleanUrl = new URL(url.replace(/\/$/g, ''))
    cleanUrl.searchParams.delete('code')
    cleanUrl.searchParams.delete('access_token')

    if (cleanUrl.toString() !== window.location.origin) {
      dispatch(setRedirectUrl(cleanUrl.toString()))
    }
  }

  const onError = () => <ProtectedRouteError />

  // Get the query string value based provided a query string parameter name.
  const getQueryStringValue = (parmName: string): string | null => {
    const query = window.location.search.substring(1)
    const splitParms = query.split('&')
    for (let i = 0; i < splitParms.length; i += 1) {
      const pair = splitParms[i].split('=')
      if (pair[0] === parmName) {
        return pair[1]
      }
    }
    return null
  }

  const getPathName = (pathName: string): boolean => {
    const path = window.location.pathname.substring(1)
    if (path === pathName) {
      return true
    }
    return false
  }

  // If the environment variables failed to load, display the unavailable page.
  if (!APP_REACT_AUTH_AUTHORITY) {
    return (
      <BrowserRouter>
        <Box sx={styles.unavailableContainer}>
          <Unavailable />
        </Box>
      </BrowserRouter>
    )
  }

  if (getPathName('unsubscribe')) {
    return (
      <BrowserRouter>
        <Routes>
          <Route
            path="/unsubscribe"
            element={
              <ProtectedRoute matchType="all" queryParams={['id', 'qs']}>
                <Unsubscribe />
              </ProtectedRoute>
            }
          />
          <Route path="/unsubscribe-success" element={<UnsubscribeSuccess />} />
        </Routes>
      </BrowserRouter>
    )
  }

  const showTryAgain = true

  return (
    <OidcIdentity
      redirect_uri={window.location.origin}
      authority={APP_REACT_AUTH_AUTHORITY}
      client_id={APP_REACT_AUTH_CLIENTID}
      scope={APP_REACT_AUTH_SCOPE}
      autoLogin={autoLogin}
      redirectAfterLogout={autoRedirect}
      postLogoutRedirectUri={redirectURI}
      onDeepLinkRedirectCallback={handleRedirectCallback}
      silentRequestTimeoutInSeconds={60}>
      <Anonymous>
        <Interceptor>
          <BrowserRouter>
            {getQueryStringValue(errorDescriptionQueryStringParm) ===
              unauthorizedForAppErrorMessage && (
              <Box sx={styles.unavailableContainer}>
                <Unavailable
                  returnUrl="#"
                  headerTextKey="Authorization.NotAuthorizedForApplication.NotAuthorizedHeader"
                  subHeaderTextheaderTextKey="Authorization.NotAuthorizedForApplication.NotAuthorizedReason"
                  subHeader2ndLineTextheaderTextKey="Authorization.NotAuthorizedForApplication.NotAuthorizedPrimarySuggestion"
                  subHeader3rdLineTextheaderTextKey="Authorization.NotAuthorizedForApplication.NotAuthorizedSecondarySuggestion"
                  showTryAgain={false}
                  showRsmLogo={showRsmLogoOnAuthFailure}
                />
              </Box>
            )}
            {getQueryStringValue(errorDescriptionQueryStringParm) ===
              userCreationDisabledErrorMessage && (
              <Box sx={styles.unavailableContainer}>
                <Unavailable
                  returnUrl={window.location.origin}
                  headerTextKey="Authorization.NotAuthorizedForApplication.NotAuthorizedHeader"
                  subHeaderTextheaderTextKey="Authorization.NotAuthorizedForApplication.NotAuthorizedReason"
                  subHeader2ndLineTextheaderTextKey="Authorization.NotAuthorizedForApplication.NotAuthorizedPrimarySuggestion"
                  subHeader3rdLineTextheaderTextKey="Authorization.NotAuthorizedForApplication.NotAuthorizedSecondarySuggestion"
                  showTryAgain={showTryAgain}
                  showRsmLogo={showRsmLogoOnAuthFailure}
                />
              </Box>
            )}
            {getQueryStringValue(errorDescriptionQueryStringParm) !== null &&
              getQueryStringValue(errorDescriptionQueryStringParm) !==
                userCreationDisabledErrorMessage &&
              getQueryStringValue(errorDescriptionQueryStringParm) !==
                unauthorizedForAppErrorMessage && (
                <Box sx={styles.unavailableContainer}>
                  <Unavailable
                    returnUrl="#"
                    showTryAgain={false}
                    showRsmLogo={showRsmLogoOnAuthFailure}
                  />
                </Box>
              )}
          </BrowserRouter>
        </Interceptor>
      </Anonymous>
      <Authenticated>
        <Interceptor>
          <>
            <VersionService />
            <BrowserRouter>
              <DeepLink />
              <AppInsightsErrorBoundary
                onError={() => onError()}
                appInsights={rcPlugin}>
                <Eula host={IDM_EULA_SERVICE_URL} tokenFactory={getToken}>
                  <SessionManager />
                  <TrackingManager />
                  <Refocus>
                    <AppInsightsContext.Provider value={rcPlugin}>
                      <ScrollToTop />
                      <AppRoutes />
                    </AppInsightsContext.Provider>
                  </Refocus>
                </Eula>
              </AppInsightsErrorBoundary>
            </BrowserRouter>
          </>
        </Interceptor>
      </Authenticated>
    </OidcIdentity>
  )
}

export default App
